import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CheckCircle2, MessageSquareHeart } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import { Seo } from "@/components/Seo";
import { FeedbackForm, FeedbackSource } from "@/components/FeedbackForm";

const VALID_SOURCES: FeedbackSource[] = ["designer", "mobile_results", "settings", "direct"];

const FeedbackAndTestimonials = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  const rawSource = params.get("source");
  const source: FeedbackSource = (VALID_SOURCES as string[]).includes(rawSource || "")
    ? (rawSource as FeedbackSource)
    : "direct";
  const designId = params.get("designId");

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate(`/auth?redirect=${encodeURIComponent("/feedback-and-testimonials" + window.location.search)}`);
        return;
      }
      setUser(session.user);
      setLoading(false);
    };
    init();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title="Share Feedback & Testimonials — Decor Canvas"
        description="Rate your experience and share feedback or a testimonial with the Decor Canvas team."
      />

      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <Card className="p-6 sm:p-8 shadow-card">
          <div className="flex items-center gap-3 mb-2">
            <MessageSquareHeart className="w-7 h-7 text-primary" />
            <h1 className="text-2xl sm:text-3xl font-bold">Share Your Feedback</h1>
          </div>
          <p className="text-muted-foreground mb-6">
            Your feedback helps us make Decor Canvas better. Rate your experience and tell us what you think.
          </p>

          {submitted ? (
            <div className="text-center py-10 space-y-4">
              <CheckCircle2 className="w-14 h-14 mx-auto text-primary" />
              <h2 className="text-xl font-semibold">Thank you!</h2>
              <p className="text-muted-foreground">
                We've received your feedback. We truly appreciate you taking the time.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                <Button variant="outline" onClick={() => setSubmitted(false)}>
                  Submit another
                </Button>
                <Button onClick={() => navigate("/")}>Back to home</Button>
              </div>
            </div>
          ) : (
            user && (
              <FeedbackForm
                source={source}
                relatedDesignId={designId}
                userId={user.id}
                userEmail={user.email}
                userName={(user.user_metadata?.full_name as string) || null}
                onSubmitted={() => setSubmitted(true)}
              />
            )
          )}
        </Card>
      </div>
    </div>
  );
};

export default FeedbackAndTestimonials;
